import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ChevronRight } from 'lucide-react';
import { ProjectTree } from './ProjectTree';
import { Palette } from './Palette';
import { ResizeHandle, loadPanelWidth } from './ResizeHandle';
import { activeSheet, useStore } from '../store';

const COLLAPSE_KEY = 'skema:sidebar-collapsed';
const WIDTH_KEY = 'skema:sidebar-width';
const TAB_KEY = 'skema:sidebar-tab';
const SIDEBAR_MIN = 210;
const SIDEBAR_MAX = 560;

type SidebarTab = 'projects' | 'library';

/**
 * Altium-style docked left panel. Instead of stacking the project tree and the
 * component library in an accordion, each is its own panel selectable from a
 * tab strip at the BOTTOM of the dock (like Altium's "Projects / PCB / PCB
 * Filter" tabs). The dock is width-resizable and can collapse to a thin rail.
 */
export function Sidebar() {
  const [width, setWidth] = useState<number>(() =>
    loadPanelWidth(WIDTH_KEY, 268, SIDEBAR_MIN, SIDEBAR_MAX),
  );
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [tab, setTab] = useState<SidebarTab>(() => {
    try {
      return localStorage.getItem(TAB_KEY) === 'library' ? 'library' : 'projects';
    } catch {
      return 'projects';
    }
  });

  const activeSheetKind = useStore((s) => activeSheet(s).kind);
  const projectOnly = activeSheetKind === 'interconnect';
  // The library is meaningless on the interconnect sheet — force Projects there.
  const effectiveTab: SidebarTab = projectOnly ? 'projects' : tab;
  const wrapRef = useRef<HTMLElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
      localStorage.setItem(TAB_KEY, tab);
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      // ignore
    }
  }, [collapsed, tab, width]);

  // NOTE: keep this early return AFTER all hooks above.
  if (collapsed) {
    return (
      <aside style={collapsedRailStyle}>
        <button
          onClick={() => setCollapsed(false)}
          title="Show panels"
          style={railBtnStyle}
        >
          <ChevronRight size={15} />
        </button>
        <div style={railLabelStyle}>PANELS</div>
      </aside>
    );
  }

  return (
    <aside
      ref={wrapRef}
      style={{
        width,
        background: 'var(--panel)',
        borderRight: '1px solid var(--panel-border)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <ResizeHandle edge="right" width={width} setWidth={setWidth} min={SIDEBAR_MIN} max={SIDEBAR_MAX} />

      {/* Active panel body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {effectiveTab === 'projects' ? (
          <ProjectTree onCollapse={() => setCollapsed(true)} />
        ) : (
          <Palette />
        )}
      </div>

      {/* Bottom tab strip (Altium-style) */}
      {!projectOnly && (
        <div style={tabStripStyle}>
          <TabButton label="Projects" active={effectiveTab === 'projects'} onClick={() => setTab('projects')} />
          <TabButton label="Library" active={effectiveTab === 'library'} onClick={() => setTab('library')} />
        </div>
      )}
    </aside>
  );
}

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...tabBtnStyle,
        color: active ? 'var(--text)' : 'var(--text-muted)',
        background: active ? 'var(--panel)' : 'transparent',
        borderTop: active ? '2px solid var(--accent)' : '2px solid transparent',
      }}
    >
      {label}
    </button>
  );
}

const tabStripStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  flexShrink: 0,
  height: 26,
  background: 'linear-gradient(var(--chrome-bottom), var(--chrome-top))',
  borderTop: '1px solid var(--chrome-border)',
};

const tabBtnStyle: CSSProperties = {
  flex: 1,
  border: 'none',
  borderRight: '1px solid var(--panel-border)',
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  cursor: 'pointer',
  padding: 0,
};

const collapsedRailStyle: CSSProperties = {
  width: 32,
  flexShrink: 0,
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderRight: '1px solid var(--chrome-border)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: 6,
  gap: 10,
};

const railBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--btn-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  padding: 0,
};

const railLabelStyle: CSSProperties = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  userSelect: 'none',
};
